/**
 * Returning Patient Arc
 *
 * Question: "Is this patient a returning patient, and which visit is this for?"
 *
 * Entry: phoneInput (new) or infoReview (re-invoked)
 * Exit: done (final) — produces ReturningPatientOutput
 *
 * Actors: checkReturningPatient, disambiguate, sendSMS, verifySMS, fetchReturningVisitData
 * Signals:
 *   GO_BACK (from phoneInput — first state, arc boundary)
 *   NEW_PATIENT (from newPatientInterstitial — patient wants new patient flow)
 *   GO_TO_UPDATE (from infoReview — patient wants to update their info)
 *
 * Navigation map: see README.md
 */

import { assign, sendParent, setup } from "xstate";
import { classifyError, logInhabitant } from "../../lib/kiosk-inhabitant-log";
import type {
  ReturningPatientInput,
  ReturningPatientOutput,
} from "../contracts/arc-contracts";
import type { KioskMachineContext, KioskMachineEvent } from "../types";

// Actors
import { checkReturningPatientActor } from "../actors/check-returning-patient.actor";
import { disambiguateActor } from "../actors/disambiguate.actor";
import { fetchReturningVisitDataActor } from "../actors/fetch-returning-visit-data.actor";
import { matchByEmailActor } from "../actors/match-by-email.actor";
import { sendSMSActor } from "../actors/send-sms.actor";
import { verifySMSActor } from "../actors/verify-sms.actor";

import { isSmsAuthenticated, otpInvalid } from "../guards/sms.guard";
// Guards
import { hasCompletedBefore } from "../helper/arc-helpers";
import {
  type InsuranceShape,
  pickInsuranceFields,
} from "../helper/insurance-helpers";

// Actions
import { clearContactInfo } from "../actions/clear-contact-info.action";
import { clearInsurance } from "../actions/clear-insurance.action";
import { clearUserIds } from "../actions/clear-user-ids.action";
import { saveActorResult } from "../actions/save-actor-result.action";
import { saveError } from "../actions/save-error.action";
import { saveFormData } from "../actions/save-form-data.action";
import { saveMatchedPatientData } from "../actions/save-matched-patient-data.action";

/**
 * Seed arc context from input.
 * Uses KioskMachineContext (wide type) initially — see AGENTS.md Typing Strategy.
 */
function createReturningPatientContext(
  input: ReturningPatientInput,
): KioskMachineContext {
  // If re-invoked, restore from previous output and flag for routing
  if (input.previousOutput) {
    return {
      orgId: input.orgId,
      orgName: input.orgName,
      practiceId: input.practiceId,
      language: input.language,
      ...input.previousOutput,
      // Parent's responses take precedence over stale previousOutput.responses
      // (e.g., user signed forms in intake, backed out, responses were preserved in parent)
      responses: input.responses ?? input.previousOutput.responses,
      // Map output field names back to internal _consumer* field names
      _consumerAddress: input.previousOutput.address,
      _consumerCity: input.previousOutput.city,
      _consumerState: input.previousOutput.state,
      _consumerZip: input.previousOutput.zip,
      _consumerAddressLine2: input.previousOutput.addressLine2,
      // Pharmacy
      _consumerPharmacyName: input.previousOutput.pharmacyName,
      _consumerPharmacyPhone: input.previousOutput.pharmacyPhone,
      _consumerPharmacyStreetAddress:
        input.previousOutput.pharmacyStreetAddress,
      _consumerPharmacyCity: input.previousOutput.pharmacyCity,
      _consumerPharmacyState: input.previousOutput.pharmacyState,
      _consumerPharmacyZip: input.previousOutput.pharmacyZip,
      _consumerPharmacyEhrId: input.previousOutput.pharmacyEhrId,
      _consumerPharmacyNcpdpId: input.previousOutput.pharmacyNcpdpId,
      // Emergency contact
      _consumerEmergencyContactFirstName:
        input.previousOutput.emergencyContactFirstName,
      _consumerEmergencyContactLastName:
        input.previousOutput.emergencyContactLastName,
      _consumerEmergencyContactPhone:
        input.previousOutput.emergencyContactPhone,
      _consumerEmergencyContactRelationship:
        input.previousOutput.emergencyContactRelationship,
      // PCP
      _consumerPcpFirstName: input.previousOutput.pcpFirstName,
      _consumerPcpLastName: input.previousOutput.pcpLastName,
      _consumerPcpPhone: input.previousOutput.pcpPhone,
      _consumerPcpAddress: input.previousOutput.pcpAddress,
      _consumerPcpCity: input.previousOutput.pcpCity,
      _consumerPcpState: input.previousOutput.pcpState,
      _consumerPcpZip: input.previousOutput.pcpZip,
      _consumerPcpEhrId: input.previousOutput.pcpEhrId,
      // Referral
      _consumerReferralSource: input.previousOutput.referralSource,
      // Restore appointments for hadAppointments guard
      _appointments: input.previousOutput.appointments,
      // Triage answers (needed by NewPatientInterstitial)
      planToUseInsurance: input.planToUseInsurance,
      isTravelInsurance: input.isTravelInsurance,
      under18: input.under18,
      checkingInForSomeoneElse: input.checkingInForSomeoneElse,
      // Restore appointment selection for pre-selection on back navigation
      _selectedAppointmentId: input.previousOutput.selectedAppointmentId,
      _selectedEhrAppointmentId: input.previousOutput.selectedEhrAppointmentId,
      _selectedAppointment: input.previousOutput.selectedAppointment,
      // Restore HIPAA + info confirmation state for correct intake routing
      _hipaaExpired: input.previousOutput.hipaaExpired,
      _infoConfirmed: input.previousOutput.infoConfirmed,
      _reInvoked: true,
    };
  }

  // Email match mode: entering arc from identity arc's email match signal
  if (input.emailMatchMode) {
    return {
      orgId: input.orgId,
      orgName: input.orgName,
      practiceId: input.practiceId,
      language: input.language,
      intakeFormSections: input.intakeFormSections,
      responses: input.responses,
      formIds: input.formIds,
      phoneNumber: input.phoneNumber,
      dateOfBirth: input.dateOfBirth,
      email: input.emailForMatch,
      firstName: input.firstNameForMatch,
      lastName: input.lastNameForMatch,
      planToUseInsurance: input.planToUseInsurance,
      isTravelInsurance: input.isTravelInsurance,
      under18: input.under18,
      checkingInForSomeoneElse: input.checkingInForSomeoneElse,
      _emailMatchMode: true,
    };
  }

  // BACK from identity arc after newPatientInterstitial: resume at that screen
  if (input.newPatientPath) {
    return {
      orgId: input.orgId,
      orgName: input.orgName,
      practiceId: input.practiceId,
      language: input.language,
      intakeFormSections: input.intakeFormSections,
      responses: input.responses,
      formIds: input.formIds,
      phoneNumber: input.phoneNumber,
      dateOfBirth: input.dateOfBirth,
      planToUseInsurance: input.planToUseInsurance,
      isTravelInsurance: input.isTravelInsurance,
      under18: input.under18,
      checkingInForSomeoneElse: input.checkingInForSomeoneElse,
      _newPatientPath: true,
    };
  }

  return {
    orgId: input.orgId,
    orgName: input.orgName,
    practiceId: input.practiceId,
    language: input.language,
    // Intake data (passed through to intake arc)
    intakeFormSections: input.intakeFormSections,
    responses: input.responses,
    formIds: input.formIds,
    // Triage answers (needed by NewPatientInterstitial)
    planToUseInsurance: input.planToUseInsurance,
    isTravelInsurance: input.isTravelInsurance,
    under18: input.under18,
    checkingInForSomeoneElse: input.checkingInForSomeoneElse,
  };
}

function buildInsurancesOutput(
  context: KioskMachineContext,
): ReturningPatientOutput["insurances"] {
  // Always use stale match data — reprice data lives in parent context and is evaluated there
  const stale = context._consumerInsurancesFromMatch as
    | InsuranceShape[]
    | null
    | undefined;
  if (!stale?.length) return undefined;

  const stalePrimary = stale.find((i) => i.priority === 1);
  const staleSecondary = stale.find((i) => i.priority === 2);

  if (!stalePrimary) return undefined;

  return {
    primaryInsurance: pickInsuranceFields(stalePrimary),
    secondaryInsurance: staleSecondary
      ? pickInsuranceFields(staleSecondary)
      : undefined,
  };
}

/**
 * Extract typed output from arc context at final state.
 */
function extractReturningPatientOutput(
  context: KioskMachineContext,
): ReturningPatientOutput {
  return {
    // Auth
    userId: context.userId,
    consumerId: context.consumerId,
    ehrPatientId: context.ehrPatientId as string | undefined,
    verified: context.verified,
    phoneNumber: context.phoneNumber as string | undefined,
    email: context.email as string | undefined,
    // Demographics
    firstName: context.firstName as string | undefined,
    lastName: context.lastName as string | undefined,
    dateOfBirth: context.dateOfBirth as string | undefined,
    sex: context.sex as string | undefined,
    // Address
    address: context._consumerAddress as string | undefined,
    city: context._consumerCity as string | undefined,
    state: context._consumerState as string | undefined,
    zip: context._consumerZip as string | undefined,
    addressLine2: context._consumerAddressLine2 as string | undefined,
    // Appointment
    selectedAppointmentId: context._selectedAppointmentId as string | undefined,
    selectedEhrAppointmentId: context._selectedEhrAppointmentId as
      | string
      | undefined,
    selectedAppointment: context._selectedAppointment as
      | ReturningPatientOutput["selectedAppointment"]
      | undefined,
    appointments:
      context._appointments as ReturningPatientOutput["appointments"],
    // Pharmacy
    pharmacyName: context._consumerPharmacyName as string | undefined,
    pharmacyPhone: context._consumerPharmacyPhone as string | undefined,
    pharmacyStreetAddress: context._consumerPharmacyStreetAddress as
      | string
      | undefined,
    pharmacyCity: context._consumerPharmacyCity as string | undefined,
    pharmacyState: context._consumerPharmacyState as string | undefined,
    pharmacyZip: context._consumerPharmacyZip as string | undefined,
    pharmacyEhrId: context._consumerPharmacyEhrId as string | undefined,
    pharmacyNcpdpId: context._consumerPharmacyNcpdpId as string | undefined,
    // Emergency contact
    emergencyContactFirstName: context._consumerEmergencyContactFirstName as
      | string
      | undefined,
    emergencyContactLastName: context._consumerEmergencyContactLastName as
      | string
      | undefined,
    emergencyContactPhone: context._consumerEmergencyContactPhone as
      | string
      | undefined,
    emergencyContactRelationship:
      context._consumerEmergencyContactRelationship as string | undefined,
    // PCP
    pcpFirstName: context._consumerPcpFirstName as string | undefined,
    pcpLastName: context._consumerPcpLastName as string | undefined,
    pcpPhone: context._consumerPcpPhone as string | undefined,
    pcpAddress: context._consumerPcpAddress as string | undefined,
    pcpCity: context._consumerPcpCity as string | undefined,
    pcpState: context._consumerPcpState as string | undefined,
    pcpZip: context._consumerPcpZip as string | undefined,
    pcpEhrId: context._consumerPcpEhrId as string | undefined,
    // Referral
    referralSource: context._consumerReferralSource as string | undefined,
    infoConfirmed: (context._infoConfirmed as boolean) ?? false,
    hipaaExpired: (context._hipaaExpired as boolean) ?? true,
    otpDeclined: context.otpDeclined as boolean | undefined,
    // Pass-through
    intakeFormSections: context.intakeFormSections as
      | ReturningPatientOutput["intakeFormSections"]
      | undefined,
    responses: context.responses as Record<string, unknown> | undefined,
    formIds: context.formIds as number[] | undefined,
    // Stale insurance from match API (reprice lives in parent context)
    insurances: buildInsurancesOutput(context),
  };
}

// ─── Day-1 inhabitant logging helpers (no behavior change) ───
// See plans/day1-error-legibility.md. Pulls the ENTERED identity from context
// so on-site bucketing can answer "user-created vs system-created?".

// biome-ignore lint/suspicious/noExplicitAny: arc context is the wide KioskMachineContext
function enteredIdentity(context: any) {
  return {
    firstNameEntered: context.firstName as string | undefined,
    lastNameEntered: context.lastName as string | undefined,
    phoneNormalized: (context.phoneNumber as string | undefined)?.replace(
      /^\+1/,
      "",
    ),
    phoneRaw: context.phoneNumber as string | undefined,
    dobEntered: context.dateOfBirth as string | undefined,
    consumerId: context.consumerId as string | undefined,
    userId: context.userId as string | undefined,
    // Per-clinic facet — lets failures be sliced by location (RUM usr.practiceID
    // is empty in prod, so this is currently the only structured practice signal).
    practiceId: context.practiceId as string | undefined,
    orgId: context.orgId as number | undefined,
    consumerIdWasSet: !!context.consumerId,
    reInvoked: !!context._reInvoked,
    isEmailMatchMode: !!context._emailMatchMode,
  };
}

// Disambiguation-only context: the field the backend asked about + the
// patient-typed answer(s). This is PHI beyond the base subset (address/email/zip),
// logged ONLY on disambiguation-resolution outcomes where the typed answer is the
// signal for user-created vs system-created. See plans/day1-error-legibility.md.
// biome-ignore lint/suspicious/noExplicitAny: arc context is the wide KioskMachineContext
function disambigContext(context: any) {
  return {
    disambiguationField: context.disambiguationField as string | undefined,
    disambiguationAnswer: context.disambiguationAnswer as
      | Record<string, unknown>
      | undefined,
  };
}

// ─── Machine Definition ──────────────────────────────────────

export const returningPatientArc = setup({
  types: {
    context: {} as KioskMachineContext,
    events: {} as KioskMachineEvent,
    input: {} as ReturningPatientInput,
    output: {} as ReturningPatientOutput,
  },
  actors: {
    checkReturningPatient: checkReturningPatientActor.impl,
    disambiguate: disambiguateActor.impl,
    sendSMS: sendSMSActor.impl,
    verifySMS: verifySMSActor.impl,
    // biome-ignore lint/suspicious/noExplicitAny: XState actor params are complex
    fetchReturningVisitData: fetchReturningVisitDataActor.impl as any,
    matchByEmail: matchByEmailActor.impl,
  },
  guards: {
    isNewPatientPath: ({ context }) => !!context._newPatientPath,
    isEmailMatchMode: ({ context }) => !!context._emailMatchMode,
    hasCompletedBefore,
    // biome-ignore lint/suspicious/noExplicitAny: XState guard params are complex
    isSmsAuthenticated: isSmsAuthenticated.impl as any,
    // biome-ignore lint/suspicious/noExplicitAny: XState guard params are complex
    otpInvalid: otpInvalid.impl as any,
    hasOneAppointment: ({ context }) =>
      (context._appointments as unknown[])?.length === 1,
    hasMultipleAppointments: ({ context }) =>
      (context._appointments as unknown[])?.length >= 2,
    hasNoAppointments: ({ context }) =>
      !(context._appointments as unknown[])?.length,
    hadAppointments: ({ context }) =>
      !!(context._appointments as unknown[])?.length,
  },
  actions: {
    // biome-ignore lint/suspicious/noExplicitAny: XState action params are complex
    saveFormData: saveFormData.impl as any,
    // biome-ignore lint/suspicious/noExplicitAny: XState action params are complex
    saveError: saveError.impl as any,
    // biome-ignore lint/suspicious/noExplicitAny: XState action params are complex
    saveActorResult: saveActorResult.impl as any,
    // biome-ignore lint/suspicious/noExplicitAny: XState action params are complex
    clearUserIds: clearUserIds.impl as any,
    // biome-ignore lint/suspicious/noExplicitAny: XState action params are complex
    clearContactInfo: clearContactInfo.impl as any,
    // biome-ignore lint/suspicious/noExplicitAny: XState action params are complex
    clearInsurance: clearInsurance.impl as any,
    // biome-ignore lint/suspicious/noExplicitAny: XState cross-arc sendParent event typing
    signalEmailMatchFailed: sendParent(({ context }: any) => ({
      type: "EMAIL_MATCH_FAILED" as const,
      ...(context.otpDeclined ? { otpDeclined: true } : {}),
    })),
    // biome-ignore lint/suspicious/noExplicitAny: XState action params are complex
    saveMatchedPatientData: saveMatchedPatientData.impl as any,
    // Named so tests can provide a no-op override (arc runs without a parent in isolation)
    // biome-ignore lint/suspicious/noExplicitAny: XState cross-arc sendParent action typing
    signalSpawnReprice: sendParent(({ context, event }: any) => ({
      type: "SPAWN_REPRICE" as const,
      consumerId: context.consumerId ?? event.output?.consumerId ?? null,
      orgId: context.orgId,
      firstName: context.firstName,
      lastName: context.lastName,
      dateOfBirth: context.dateOfBirth,
    })),

    // ─── Day-1 inhabitant logging (side-effect only, no context/target change) ───
    // The default branch catches BOTH the expected "NO_PATIENTS_MATCHED" (a
    // normal new-patient lookup — not a failure) and a genuinely unrecognized
    // discriminant (the inhabitant). Only the latter is the system-fault event;
    // logging the former as such would flood the facet and poison aggregates.
    // biome-ignore lint/suspicious/noExplicitAny: XState action params are complex
    logDefaultMatchBranch: ({ context, event }: any) => {
      const discriminant = (event.output as { message?: string })?.message;
      if (discriminant === "NO_PATIENTS_MATCHED") {
        // Expected no-match: benign baseline only, never a fault.
        logInhabitant(
          {
            inhabitant: "returningPatient.matchResult.outcome",
            branch: "no_match",
            discriminant,
            faultHint: "ambiguous",
            ...enteredIdentity(context),
          },
          "info",
        );
        return;
      }
      // Genuinely unrecognized discriminant (incl. undefined): the inhabitant.
      logInhabitant({
        inhabitant:
          "returningPatient.matchResult.unknownDiscriminantFallsThrough",
        branch: "default",
        discriminant,
        cause: "unknown_discriminant",
        faultHint: "system",
        ...enteredIdentity(context),
      });
    },
    // onError on a lookup/send actor aliases a system failure to "new patient".
    // biome-ignore lint/suspicious/noExplicitAny: XState action params are complex
    logMatchedAsNew: ({ context, event }: any) => {
      const { faultHint, cause } = classifyError(event.error);
      logInhabitant({
        inhabitant: "returningPatient.correctInput.matchedAsNew",
        branch: "onError",
        cause,
        faultHint,
        ...enteredIdentity(context),
      });
    },
    // Baseline: a real match succeeded — gives the match-rate denominator.
    // biome-ignore lint/suspicious/noExplicitAny: XState action params are complex
    logMatchOutcome: ({ context, event }: any) =>
      logInhabitant(
        {
          inhabitant: "returningPatient.matchResult.outcome",
          branch: "matched",
          discriminant: (event.output as { message?: string })?.message,
          faultHint: "ambiguous",
          ...enteredIdentity(context),
        },
        "info",
      ),
    // Disambiguation exhausted (attempts>=1) → routed to new-patient.
    // biome-ignore lint/suspicious/noExplicitAny: XState action params are complex
    logDisambiguationExhausted: ({ context }: any) =>
      logInhabitant({
        inhabitant: "returningPatient.disambiguation.backstopToNewPatient",
        branch: "multiple_exhausted",
        cause: "disambiguation_exhausted",
        faultHint: "system",
        attemptCount: (context.disambiguationAttempts as number) ?? 0,
        ...enteredIdentity(context),
        ...disambigContext(context),
      }),
    // Disambiguation resolved to a definite outcome (match or no-match). Reveals
    // the patient-typed answer so a wrong outcome can be judged user- vs system-side.
    // biome-ignore lint/suspicious/noExplicitAny: XState action params are complex
    logDisambiguationResolved: ({ context, event }: any) => {
      const discriminant = (event.output as { message?: string })?.message;
      logInhabitant(
        {
          inhabitant: "returningPatient.disambiguation.resolved",
          branch: discriminant === "PATIENT_MATCHED" ? "matched" : "no_match",
          discriminant,
          faultHint: "ambiguous",
          attemptCount: (context.disambiguationAttempts as number) ?? 0,
          ...enteredIdentity(context),
          ...disambigContext(context),
        },
        "info",
      );
    },
    // User pressed SKIP on OTP despite a confirmed match.
    // biome-ignore lint/suspicious/noExplicitAny: XState action params are complex
    logOtpSkipped: ({ context }: any) =>
      logInhabitant({
        inhabitant: "returningPatient.otp.userSkippedButWasReturning",
        branch: "skip",
        cause: "user_skip",
        faultHint: "ambiguous",
        ...enteredIdentity(context),
      }),
    // On the skip-confirm screen, the patient chose to go back (Verify my code / back
    // arrow) instead of skipping. The deterrence signal: a high reconsider rate means
    // the confirm screen is working (PLA-2006). Pairs with logOtpSkipped.
    // biome-ignore lint/suspicious/noExplicitAny: XState action params are complex
    logOtpSkipReconsidered: ({ context }: any) =>
      logInhabitant({
        inhabitant: "returningPatient.otp.skipReconsideredAfterPrompt",
        branch: "skip_reconsidered",
        cause: "user_reconsidered",
        faultHint: "ambiguous",
        ...enteredIdentity(context),
      }),
    // fetchReturningVisitData failed — conflated downstream with "no appointments".
    // biome-ignore lint/suspicious/noExplicitAny: XState action params are complex
    logFetchDataError: ({ context, event }: any) => {
      const { faultHint, cause } = classifyError(event.error);
      logInhabitant({
        inhabitant: "returningPatient.fetchData.foundButNoAppointments",
        branch: "fetch_error",
        cause,
        faultHint,
        ...enteredIdentity(context),
      });
    },
  },
}).createMachine({
  id: "returningPatient",
  context: ({ input }) => createReturningPatientContext(input),
  output: ({ context }) => extractReturningPatientOutput(context),
  initial: "route",

  states: {
    // ===== ENTRY ROUTING =====
    route: {
      always: [
        // Email match mode: enter via email match path
        { guard: "isEmailMatchMode", target: "matchingByEmail" },
        // BACK from identity after newPatientInterstitial
        { guard: "isNewPatientPath", target: "newPatientInterstitial" },
        // Re-invocation: route through field check (guard already passed on first run,
        // but re-validating is defensive against partial context restoration)
        { guard: "hasCompletedBefore", target: "infoReviewRouting" },
        // First invocation: start from the beginning
        { target: "phoneInput" },
      ],
    },

    // ===== RETURNING PATIENT LOOKUP =====
    phoneInput: {
      meta: {
        componentName: "ContactInput",
        contentKey: "medrite.checkin.contact-input-phone",
      },
      on: {
        SUBMIT: {
          target: "lookupDobInput",
          actions: ["saveFormData"],
        },
        BACK: {
          // Arc boundary: signal parent to go back (to triaging)
          actions: sendParent({ type: "GO_BACK" }),
        },
      },
    },

    lookupDobInput: {
      meta: {
        componentName: "DOBInput",
        contentKey: "medrite.checkin.dob-input",
      },
      on: {
        SUBMIT: {
          target: "checkingForReturningPatient",
          actions: ["saveFormData"],
        },
        BACK: { target: "phoneInput" },
      },
    },

    matchingByEmail: {
      meta: {
        componentName: "LoadingSpinner",
        contentKey: "medrite.checkin.processing-id",
      },
      invoke: {
        id: "matchByEmail",
        src: "matchByEmail",
        input: ({ context }) => ({
          email: context.email as string,
          orgId: context.orgId,
          firstName: context.firstName as string | undefined,
          lastName: context.lastName as string | undefined,
          dob: context.dateOfBirth as string | undefined,
          phoneNumber: context.phoneNumber as string | undefined,
        }),
        onDone: [
          {
            guard: ({ event }) =>
              (event.output as { message?: string })?.message ===
              "PATIENT_MATCHED",
            target: "sendingReturningPatientSMS",
            actions: ["saveMatchedPatientData", "signalSpawnReprice"],
          },
          {
            guard: ({ event }) =>
              (event.output as { message?: string })?.message ===
              "MULTIPLE_PATIENTS_MATCHED",
            target: "disambiguationQuestion",
            actions: assign({
              correlationId: ({ event }) =>
                (event.output as { correlationId?: string }).correlationId,
              disambiguationField: ({ event }) =>
                (event.output as { disambiguationField?: string })
                  .disambiguationField,
              disambiguationAttempts: (_: unknown) => 0,
              // Fresh session: clear any accumulated answers from a prior
              // disambiguation attempt (e.g. after BACK to DOB and re-match).
              disambiguationAnswer: () => undefined,
            }),
          },
          {
            // NO_PATIENTS_MATCHED (default)
            target: "emailMatchFailed",
          },
        ],
        onError: {
          target: "emailMatchFailed",
        },
      },
    },

    emailMatchFailed: {
      meta: {
        componentName: "LoadingSpinner",
        contentKey: "medrite.checkin.processing-id",
      },
      entry: "signalEmailMatchFailed",
    },

    checkingForReturningPatient: {
      meta: {
        componentName: "LoadingSpinner",
        contentKey: "medrite.checkin.processing-id",
      },
      invoke: {
        id: "checkReturningPatient",
        src: "checkReturningPatient",
        input: ({ context }) => ({
          phoneNumber: context.phoneNumber,
          dateOfBirth: context.dateOfBirth,
          orgId: context.orgId,
        }),
        onDone: [
          {
            guard: ({ event }) =>
              (event.output as { message?: string })?.message ===
              "PATIENT_MATCHED",
            target: "sendingReturningPatientSMS",
            actions: [
              "saveMatchedPatientData",
              "signalSpawnReprice",
              "logMatchOutcome",
            ],
          },
          {
            guard: ({ event }) =>
              (event.output as { message?: string })?.message ===
              "MULTIPLE_PATIENTS_MATCHED",
            target: "disambiguationQuestion",
            actions: [
              assign({
                correlationId: ({ event }) =>
                  (event.output as { correlationId?: string }).correlationId,
                disambiguationField: ({ event }) =>
                  (event.output as { disambiguationField?: string })
                    .disambiguationField,
                disambiguationAttempts: (_: unknown) => 0,
                // Fresh session: clear any accumulated answers from a prior
                // disambiguation attempt (e.g. after BACK to DOB and re-match).
                disambiguationAnswer: () => undefined,
              }),
              "logMatchOutcome",
            ],
          },
          {
            // NO_PATIENTS_MATCHED (default) — also catches any unrecognized
            // discriminant. logDefaultMatchBranch separates the expected
            // no-match (benign info baseline) from a genuinely unknown string
            // (the system-fault inhabitant), so the inhabitant facet stays clean.
            target: "newPatientInterstitial",
            actions: ["logDefaultMatchBranch"],
          },
        ],
        onError: {
          // Treat errors as no match — send to new patient flow
          target: "newPatientInterstitial",
          actions: ["logMatchedAsNew"],
        },
      },
    },

    sendingReturningPatientSMS: {
      meta: {
        componentName: "LoadingSpinner",
        contentKey: "medrite.checkin.sending-sms",
      },
      invoke: {
        id: "sendSMS",
        src: "sendSMS",
        // Email match mode: send OTP via email (patient was matched by email,
        // phone number may not be available). Normal mode: send via phone.
        input: ({ context }) =>
          context._emailMatchMode
            ? {
                userId: context.userId,
                email: context.email,
                orgName: context.orgName,
                language: context.language,
              }
            : {
                userId: context.userId,
                phoneNumber: context.phoneNumber,
                orgName: context.orgName,
                language: context.language,
              },
        onDone: {
          target: "returningPatientOtpInput",
          actions: ["saveActorResult"],
        },
        onError: [
          {
            // Email match mode: OTP failure is not a new patient — signal parent
            guard: "isEmailMatchMode",
            target: "emailMatchFailed",
          },
          {
            // Fallback: treat SMS failure as new patient to avoid blocking the flow
            target: "newPatientInterstitial",
            actions: ["logMatchedAsNew"],
          },
        ],
      },
    },

    returningPatientOtpInput: {
      meta: {
        componentName: "OTPInput",
        contentKey: "medrite.checkin.otp-input",
      },
      on: {
        SUBMIT: {
          target: "verifyingReturningPatientSMS",
          actions: ["saveFormData"],
        },
        RESEND: {
          target: "sendingReturningPatientSMS",
          actions: [assign({ error: undefined, otp: undefined })],
        },
        SKIP: [
          {
            guard: "isEmailMatchMode",
            // Email match mode: transition to emailMatchFailed which signals the parent
            // via its entry action. otpDeclined is set so the signal carries it through.
            target: "emailMatchFailed",
            actions: assign({ otpDeclined: () => true }),
          },
          {
            // Normal mode: don't skip immediately. Some returning patients tap Skip
            // reflexively before the code arrives (PLA-2006), so route to a confirm
            // screen first. The real skip happens there via SKIP_ANYWAY.
            target: "returningPatientSkipConfirm",
          },
        ],
        BACK: [
          {
            guard: "isEmailMatchMode",
            actions: [
              assign({ otp: undefined, error: undefined }),
              sendParent({ type: "GO_BACK" }),
            ],
          },
          {
            target: "lookupDobInput",
            actions: assign({ otp: undefined, error: undefined }),
          },
        ],
      },
    },

    // Skip-confirm interstitial (PLA-2006). Reached ONLY from returningPatientOtpInput
    // when a returning patient taps Skip, so a reflexive tap doesn't immediately discard
    // their existing-data lookup. "Skip anyway" is the real skip; the back arrow and
    // "Verify my code" both return to the OTP screen (patient reconsidered).
    returningPatientSkipConfirm: {
      meta: {
        componentName: "OtpSkipConfirm",
        contentKey: "medrite.checkin.otp-skip-confirm",
      },
      on: {
        // The real skip — same effect SKIP used to have on the OTP screen.
        SKIP_ANYWAY: {
          target: "newPatientInterstitial",
          actions: [
            assign({ otpDeclined: (_: unknown) => true }),
            "logOtpSkipped",
          ],
        },
        // Reconsidered — go back to enter the code. Logs the deterrence signal.
        VERIFY_CODE: {
          target: "returningPatientOtpInput",
          actions: ["logOtpSkipReconsidered"],
        },
        BACK: {
          target: "returningPatientOtpInput",
          actions: ["logOtpSkipReconsidered"],
        },
      },
    },

    verifyingReturningPatientSMS: {
      meta: {
        componentName: "LoadingSpinner",
        contentKey: "medrite.checkin.verifying-otp",
      },
      invoke: {
        id: "verifySMS",
        src: "verifySMS",
        input: ({ context }) => ({
          userId: context.userId,
          otp: context.otp,
          otpTime: context.otpTime,
        }),
        onDone: {
          // OTP verified: go directly to fetch returning visit data
          target: "fetchingData",
          actions: ["saveActorResult"],
        },
        onError: [
          {
            guard: "otpInvalid",
            target: "returningPatientOtpInput",
            actions: ["saveError", assign({ otp: undefined })],
          },
          {
            target: "returningPatientOtpError",
            actions: ["saveError"],
          },
        ],
      },
    },

    returningPatientOtpError: {
      meta: {
        componentName: "ErrorDisplay",
        contentKey: "medrite.checkin.otp-error",
      },
      on: {
        RETRY: { target: "returningPatientOtpInput" },
        BACK: [
          {
            guard: "isEmailMatchMode",
            actions: [
              assign({ otp: undefined, error: undefined }),
              sendParent({ type: "GO_BACK" }),
            ],
          },
          {
            target: "lookupDobInput",
            actions: assign({ otp: undefined, error: undefined }),
          },
        ],
      },
    },

    disambiguationQuestion: {
      meta: {
        componentName: "DisambiguationQuestion",
        contentKey: "medrite.checkin.disambiguation-question",
      },
      on: {
        SUBMIT: {
          target: "sendingDisambiguate",
          actions: assign({
            // Accumulate answers across rounds. The backend re-filters the
            // original full candidate set by ALL provided fields on every call,
            // so each round must re-send prior answers to narrow progressively.
            disambiguationAnswer: ({ context, event }) => ({
              ...((context.disambiguationAnswer as
                | Record<string, unknown>
                | undefined) ?? {}),
              ...(event as { type: string; data?: Record<string, unknown> })
                .data,
            }),
          }),
        },
        BACK: [
          {
            guard: "isEmailMatchMode",
            actions: [
              assign({ otp: () => undefined }),
              sendParent({ type: "GO_BACK" }),
            ],
          },
          {
            target: "lookupDobInput",
            actions: assign({ otp: () => undefined }),
          },
        ],
      },
    },

    sendingDisambiguate: {
      meta: {
        componentName: "LoadingSpinner",
        contentKey: "medrite.checkin.processing-id",
      },
      invoke: {
        id: "disambiguate",
        src: "disambiguate",
        input: ({ context }) =>
          context._emailMatchMode
            ? {
                // Email match mode: identify via email, include phone/dob if available
                email: context.email as string,
                orgId: context.orgId,
                correlationId: context.correlationId as string,
                ...(context.phoneNumber && {
                  phoneNumber: context.phoneNumber as string,
                }),
                ...(context.dateOfBirth && {
                  dob: context.dateOfBirth as string,
                }),
                ...(context.disambiguationAnswer as Record<string, unknown>),
              }
            : {
                phoneNumber: context.phoneNumber as string,
                dob: context.dateOfBirth as string,
                orgId: context.orgId,
                correlationId: context.correlationId as string,
                ...(context.disambiguationAnswer as Record<string, unknown>),
              },
        onDone: [
          {
            guard: ({ event }) =>
              (event.output as { message?: string })?.message ===
              "PATIENT_MATCHED",
            target: "sendingReturningPatientSMS",
            actions: [
              "saveMatchedPatientData",
              "signalSpawnReprice",
              "logDisambiguationResolved",
            ],
          },
          {
            guard: ({ context, event }) =>
              (event.output as { message?: string })?.message ===
                "MULTIPLE_PATIENTS_MATCHED" &&
              ((context.disambiguationAttempts as number) ?? 0) < 1,
            target: "disambiguationQuestion",
            actions: assign({
              disambiguationAttempts: ({ context }) =>
                ((context.disambiguationAttempts as number) ?? 0) + 1,
              disambiguationField: ({ event }) =>
                (event.output as { disambiguationField?: string })
                  .disambiguationField,
            }),
          },
          {
            // MULTIPLE_PATIENTS_MATCHED with attempts >= 1 → FE backstop (2 attempts total)
            // Email match mode: go to emailMatchFailed instead
            guard: ({ context, event }) =>
              !!context._emailMatchMode &&
              (event.output as { message?: string })?.message ===
                "MULTIPLE_PATIENTS_MATCHED",
            target: "emailMatchFailed",
          },
          {
            // MULTIPLE_PATIENTS_MATCHED with attempts >= 1 → FE backstop (2 attempts total)
            guard: ({ event }) =>
              (event.output as { message?: string })?.message ===
              "MULTIPLE_PATIENTS_MATCHED",
            target: "newPatientInterstitial",
            actions: ["logDisambiguationExhausted"],
          },
          {
            // NO_PATIENTS_MATCHED — email match mode: go to emailMatchFailed
            guard: ({ context }) => !!context._emailMatchMode,
            target: "emailMatchFailed",
          },
          {
            // NO_PATIENTS_MATCHED
            target: "newPatientInterstitial",
            actions: ["logDisambiguationResolved"],
          },
        ],
        onError: {
          target: "disambiguateError",
        },
      },
    },

    disambiguateError: {
      meta: {
        componentName: "ErrorDisplay",
        contentKey: "medrite.checkin.otp-error",
      },
      on: {
        RETRY: { target: "disambiguationQuestion" },
        BACK: [
          {
            guard: "isEmailMatchMode",
            actions: [
              assign({ otp: () => undefined }),
              sendParent({ type: "GO_BACK" }),
            ],
          },
          {
            target: "lookupDobInput",
            actions: assign({ otp: () => undefined }),
          },
        ],
      },
    },

    newPatientInterstitial: {
      meta: {
        componentName: "NewPatientInterstitial",
        contentKey: "medrite.checkin.new-patient-interstitial",
      },
      on: {
        SUBMIT: {
          // Signal parent to route to identity arc (new patient flow)
          // Carry lookup demographics so the identity arc can pre-populate
          actions: sendParent(({ context }) => ({
            type: "NEW_PATIENT",
            dateOfBirth: context.dateOfBirth,
            phoneNumber: context.phoneNumber,
            otpDeclined: context.otpDeclined,
            userId: context.userId,
            consumerId: context.consumerId,
            ehrPatientId: context.ehrPatientId,
          })),
        },
        BACK: { target: "lookupDobInput" },
      },
    },

    // ===== FETCH APPOINTMENTS =====
    fetchingData: {
      meta: {
        componentName: "LoadingSpinner",
        contentKey: "medrite.checkin.processing-id",
      },
      invoke: {
        id: "fetchReturningVisitData",
        src: "fetchReturningVisitData",
        input: ({ context }) => ({
          consumerId: context.consumerId as string,
          practiceId: context.practiceId as string | undefined,
        }),
        onDone: {
          target: "appointmentRouting",
          actions: assign({
            _appointments: ({ event }) =>
              (event.output as { appointments: unknown[] }).appointments,
          }),
        },
        onError: {
          // On fetch error, route through infoReviewRouting: if all required
          // fields are present, show info review; otherwise route to full intake
          target: "infoReviewRouting",
          actions: ["logFetchDataError"],
        },
      },
    },

    // ===== APPOINTMENT ROUTING =====
    appointmentRouting: {
      always: [
        { guard: "hasOneAppointment", target: "singleAppointmentConfirm" },
        {
          guard: "hasMultipleAppointments",
          target: "multiAppointmentSelect",
        },
        { target: "infoReviewRouting" },
      ],
    },

    // ===== SINGLE APPOINTMENT CONFIRM =====
    singleAppointmentConfirm: {
      meta: {
        componentName: "SingleAppointmentConfirm",
        contentKey: "medrite.checkin.single-appointment-confirm",
      },
      on: {
        YES: {
          target: "infoReviewRouting",
          actions: assign({
            _selectedAppointment: ({ context }) =>
              (context._appointments as unknown[])?.[0],
            _selectedAppointmentId: ({ context }) =>
              (context._appointments as Array<{ id: string }>)?.[0]?.id,
            _selectedEhrAppointmentId: ({ context }) =>
              (
                context._appointments as Array<{ ehrAppointmentId?: string }>
              )?.[0]?.ehrAppointmentId,
          }),
        },
        NO: {
          target: "infoReviewRouting",
          actions: assign({
            _selectedAppointment: () => undefined,
            _selectedAppointmentId: () => undefined,
            _selectedEhrAppointmentId: () => undefined,
          }),
        },
        BACK: [
          {
            guard: "isEmailMatchMode",
            actions: [
              assign({ otp: () => undefined }),
              sendParent({ type: "GO_BACK" }),
            ],
          },
          {
            target: "lookupDobInput",
            actions: assign({ otp: () => undefined }),
          },
        ],
      },
    },

    // ===== MULTI APPOINTMENT SELECT =====
    multiAppointmentSelect: {
      meta: {
        componentName: "MultiAppointmentSelect",
        contentKey: "medrite.checkin.multi-appointment-select",
      },
      on: {
        SELECT_APPOINTMENT: {
          target: "infoReviewRouting",
          actions: assign({
            _selectedAppointment: ({ event }) =>
              (event as unknown as { data: { appointment: unknown } }).data
                ?.appointment,
            _selectedAppointmentId: ({ event }) =>
              (
                event as unknown as {
                  data: { appointment: { id: string } };
                }
              ).data?.appointment?.id,
            _selectedEhrAppointmentId: ({ event }) =>
              (
                event as unknown as {
                  data: { appointment: { ehrAppointmentId?: string } };
                }
              ).data?.appointment?.ehrAppointmentId,
          }),
        },
        SOMETHING_ELSE: {
          target: "infoReviewRouting",
          actions: assign({
            _selectedAppointment: () => undefined,
            _selectedAppointmentId: () => undefined,
            _selectedEhrAppointmentId: () => undefined,
          }),
        },
        BACK: [
          {
            guard: "isEmailMatchMode",
            actions: [
              assign({ otp: () => undefined }),
              sendParent({ type: "GO_BACK" }),
            ],
          },
          {
            target: "lookupDobInput",
            actions: assign({ otp: () => undefined }),
          },
        ],
      },
    },

    // ===== INFO REVIEW ROUTING =====
    infoReviewRouting: {
      always: [{ target: "infoReview" }],
    },

    // ===== INFO REVIEW =====
    infoReview: {
      meta: {
        componentName: "InfoReview",
        contentKey: "medrite.checkin.info-review",
      },
      on: {
        CONFIRM_INFO: {
          target: "done",
          actions: assign({
            _infoConfirmed: () => true,
          }),
        },
        UPDATE_INFO: {
          // Signal parent to route to identity arc with reviewMode.
          // Carries the full arc output so the parent has all accumulated data,
          // same as onDone would — the arc is destroyed after this signal.
          // Override `infoConfirmed: false` — the user is editing, not
          // confirming, so downstream skip-flag derivation must not treat
          // their prior (stale) confirmation as still valid.
          actions: sendParent(({ context }) => ({
            type: "GO_TO_UPDATE",
            ...extractReturningPatientOutput(context),
            infoConfirmed: false,
          })),
        },
        BACK: [
          {
            guard: "hadAppointments",
            target: "appointmentRouting",
          },
          {
            guard: "isEmailMatchMode",
            actions: [
              assign({ otp: undefined, error: undefined }),
              sendParent({ type: "GO_BACK" }),
            ],
          },
          {
            target: "lookupDobInput",
            actions: assign({ otp: undefined, error: undefined }),
          },
        ],
      },
    },

    // ===== EXIT =====
    done: {
      type: "final" as const,
    },
  },
});
